import React, { useRef, useState, useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface BeforeAfterPair {
  id?: string;
  beforeUrl?: string;
  afterUrl?: string;
  beforeAlt?: string;
  afterAlt?: string;
  caption?: string;
}

interface BeforeAfterProps {
  headline?: string;
  subheadline?: string;
  pairs?: BeforeAfterPair[];
  ctaLabel?: string;
  ctaTarget?: string;
}

const DEFAULT_PAIRS: BeforeAfterPair[] = [
  { caption: "Full exterior detail — restored paint shine" },
  { caption: "Interior deep clean — carpet & upholstery" },
  { caption: "Ceramic coating protection applied" },
];

function ComparisonModal({ pair, onClose }: { pair: BeforeAfterPair; onClose: () => void }) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [sliderPos, setSliderPos] = useState(50);
  const isDragging = useRef(false);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.2, ease: "power2.out" });
      gsap.fromTo(contentRef.current,
        { scale: 0.94, y: 28, opacity: 0 },
        { scale: 1, y: 0, opacity: 1, duration: 0.38, ease: "power3.out", delay: 0.05 }
      );
    });
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const handleMove = (e: PointerEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setSliderPos(Math.max(5, Math.min(95, ((e.clientX - rect.left) / rect.width) * 100)));
    };
    const handleUp = () => { isDragging.current = false; };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 bg-black/92 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={contentRef}
        className="w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3 px-1">
          {pair.caption && (
            <p className="text-white/60 text-sm font-medium tracking-wide">{pair.caption}</p>
          )}
          <button
            onClick={onClose}
            className="ml-auto text-white/40 hover:text-white transition-colors p-1.5"
            aria-label="Close comparison"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          ref={containerRef}
          className="relative overflow-hidden aspect-[4/3] cursor-ew-resize select-none touch-none bg-black"
          onPointerDown={(e) => {
            isDragging.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
        >
          {pair.beforeUrl ? (
            <img
              src={pair.beforeUrl}
              alt={pair.beforeAlt ?? "Before"}
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 bg-neutral-800" />
          )}

          {pair.afterUrl ? (
            <img
              src={pair.afterUrl}
              alt={pair.afterAlt ?? "After"}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ clipPath: `inset(0 0 0 ${sliderPos}%)` }}
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 bg-neutral-600" style={{ clipPath: `inset(0 0 0 ${sliderPos}%)` }} />
          )}

          {/* Divider line + handle */}
          <div
            className="absolute inset-y-0 z-10 pointer-events-none"
            style={{ left: `${sliderPos}%`, transform: "translateX(-50%)" }}
          >
            <div className="h-full w-px bg-white/80" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-2xl">
              <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l-3 3 3 3M16 9l3 3-3 3" />
              </svg>
            </div>
          </div>

          <span className="absolute top-3 left-3 z-20 bg-black/70 text-white text-[9px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 pointer-events-none" style={{ borderRadius: "2px" }}>
            Before
          </span>
          <span className="absolute top-3 right-3 z-20 bg-primary text-primary-foreground text-[9px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 pointer-events-none" style={{ borderRadius: "2px" }}>
            After
          </span>
          <span className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 text-white/35 text-[9px] font-bold uppercase tracking-widest pointer-events-none select-none">
            ← drag to compare →
          </span>
        </div>
      </div>
    </div>
  );
}

export function BeforeAfter({ props }: SectionComponentProps) {
  const {
    headline = "The Results Speak for Themselves",
    subheadline,
    pairs = DEFAULT_PAIRS,
    ctaLabel,
    ctaTarget,
  } = props as BeforeAfterProps;

  const sectionRef = useRef<HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(".ba-headline-word",
        { yPercent: 110, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: 0.75, ease: "power3.out", stagger: 0.07,
          scrollTrigger: { trigger: ".ba-header", start: "top 88%", toggleActions: "play none none none" } }
      );
      gsap.fromTo(".ba-subheadline", { opacity: 0, y: 16 }, {
        opacity: 1, y: 0, duration: 0.6, ease: "power3.out",
        scrollTrigger: { trigger: ".ba-header", start: "top 82%", toggleActions: "play none none none" },
      });
      gsap.fromTo(".ba-card",
        { opacity: 0, y: 56 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", stagger: 0.12,
          scrollTrigger: { trigger: ".ba-grid", start: "top 85%", toggleActions: "play none none none" } }
      );
    }, el);

    return () => ctx.revert();
  }, []);

  const headlineWords = headline.split(" ");

  return (
    <section ref={sectionRef} className="py-28 px-6 bg-background border-t border-border" id="gallery">
      <div className="max-w-6xl mx-auto">
        {(headline || subheadline) && (
          <div className="ba-header text-center mb-16">
            {headline && (
              <h2 className="text-5xl md:text-7xl font-bold text-foreground mb-5 tracking-tight font-display uppercase">
                {headlineWords.map((w, i) => (
                  <React.Fragment key={i}>
                    <span className="inline-block overflow-hidden leading-[1.15] align-bottom">
                      <span className="ba-headline-word inline-block">{w}</span>
                    </span>
                    {i < headlineWords.length - 1 && " "}
                  </React.Fragment>
                ))}
              </h2>
            )}
            {subheadline && (
              <p className="ba-subheadline text-muted-foreground max-w-xl mx-auto text-base leading-relaxed">
                {subheadline}
              </p>
            )}
          </div>
        )}

        <div className={[
          "ba-grid grid gap-5",
          pairs.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2",
        ].join(" ")}>
          {pairs.map((pair, i) => (
            <div
              key={pair.id ?? i}
              className="ba-card group relative cursor-pointer overflow-hidden border border-border bg-card hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/8"
              style={{ borderRadius: "var(--radius, 2px)" }}
              onClick={() => setSelectedIndex(i)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedIndex(i); } }}
              aria-label={`View ${pair.caption ?? "before and after comparison"}`}
            >
              {/* Side-by-side thumbnails */}
              <div className="flex gap-px overflow-hidden">
                <div className="relative flex-1 overflow-hidden">
                  {pair.beforeUrl ? (
                    <img
                      src={pair.beforeUrl}
                      alt={pair.beforeAlt ?? "Before"}
                      className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full aspect-[4/3] bg-muted flex items-center justify-center">
                      <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground/50">Before</span>
                    </div>
                  )}
                  <span className="absolute top-2 left-2 bg-black/65 text-white text-[9px] font-bold uppercase tracking-[0.18em] px-2.5 py-1" style={{ borderRadius: "2px" }}>
                    Before
                  </span>
                </div>

                <div className="relative flex-1 overflow-hidden">
                  {pair.afterUrl ? (
                    <img
                      src={pair.afterUrl}
                      alt={pair.afterAlt ?? "After"}
                      className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full aspect-[4/3] bg-muted flex items-center justify-center">
                      <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground/50">After</span>
                    </div>
                  )}
                  <span className="absolute top-2 right-2 bg-primary text-primary-foreground text-[9px] font-bold uppercase tracking-[0.18em] px-2.5 py-1" style={{ borderRadius: "2px" }}>
                    After
                  </span>
                </div>
              </div>

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-colors duration-300 flex items-center justify-center pointer-events-none">
                <div
                  className="flex items-center gap-2 bg-white text-black text-[10px] font-bold uppercase tracking-widest px-5 py-2.5 opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300"
                  style={{ borderRadius: "2px" }}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  View Comparison
                </div>
              </div>

              {pair.caption && (
                <div className="px-4 py-3 border-t border-border">
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">{pair.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {ctaLabel && (
          <div className="mt-14 text-center">
            <a
              href={ctaTarget ?? "#services"}
              className="inline-flex items-center justify-center bg-primary text-primary-foreground px-10 py-4 text-sm font-bold uppercase tracking-widest hover:opacity-90 transition-opacity font-display"
              style={{ borderRadius: "var(--radius, 2px)" }}
            >
              {ctaLabel}
            </a>
          </div>
        )}
      </div>

      {selectedIndex !== null && (
        <ComparisonModal
          pair={pairs[selectedIndex]!}
          onClose={() => setSelectedIndex(null)}
        />
      )}
    </section>
  );
}
